import { useCallback } from "react";
import { Flex, Spinner } from "@radix-ui/themes";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { useUploadQuackchatAssest } from "@/api/quackchat/configuration/useUploadQuackchatAssest";
import { LayoutConfigurationHeader } from "./components/LayoutConfigurationHeader";
import { QuackchatChannelLayoutPreview } from "./QuackchatChannelLayoutPreview";
import { useQuackchatLayoutState } from "./hooks/useQuackchatLayoutState";
import { HomePageSettings } from "./components/HomePageSettings";
import { GreetingSettings } from "./components/GreetingSettings";
import { AskAiSettings } from "./components/AskAiSettings";
import { SupportTicketsSettings } from "./components/SupportTicketsSettings";
import { PreChatFlowSettings } from "./components/PreChatFlowSettings";

export function QuackchatChannelLayoutConfiguration({
  simulatorId,
}: {
  simulatorId: string | undefined;
}) {
  const {
    configuration,
    isLoading,
    isReady,
    layout,
    preChat,
    isDirty,
    headerStatus,
    statusOptions,
    isSalesforceTicketSystemConnected,
    isGreetingEnabled,
    isAskAiEnabled,
    isSupportTicketsEnabled,
    endUserTicketName,
    currentTab,
    handleHomePageEnabledChange,
    handleGreetingEnabledChange,
    handleGreetingTitleChange,
    handleGreetingFallbackChange,
    handleAskAiEnabledChange,
    handleAskAiPlaceholderChange,
    handleSupportTicketsEnabledChange,
    handleSupportTicketsStyleChange,
    handleSupportTicketsStatusesChange,
    handleSupportTicketsReplyChange,
    handleEndUserTicketNameTabChange,
    handleEndUserTicketNameChange,
    handlePreChatEnabledChange,
    handlePreChatWelcomeMessageChange,
    handlePreChatWelcomeSubtitleChange,
    handlePreChatCaseTitleChange,
    handlePreChatAvatarChange,
    handlePreChatAvatarRadiusChange,
    handleSaveClick,
    handleDiscard,
  } = useQuackchatLayoutState(simulatorId);

  const preChatSurveyFlag = useFlag("preChatSurvey");

  const { mutateAsync: uploadAssest } = useUploadQuackchatAssest(simulatorId);

  const handleUploadAvatar = useCallback(
    (file: File) => uploadAssest(file),
    [uploadAssest]
  );

  if (isLoading || !isReady || configuration == null) {
    return <Spinner />;
  }

  return (
    <Flex gap="24px">
      <Flex flexGrow="1" direction="column" gap="24px">
        <LayoutConfigurationHeader
          title={"Layout"}
          status={headerStatus}
          isButtonVisible
          ctaText={"Save"}
          isButtonDisabled={!isDirty}
          onClick={handleSaveClick}
          isSecondaryButtonVisible
          secondaryButtonText={"Discard"}
          isSecondaryButtonDisabled={!isDirty}
          onSecondaryClick={handleDiscard}
        />

        <Flex direction="column">
          <HomePageSettings
            enabled={layout.enabled}
            onEnabledChange={handleHomePageEnabledChange}
          />
          {layout.enabled && (
            <>
              <GreetingSettings
                enabled={isGreetingEnabled}
                title={layout.greeting.title}
                fallbackUserName={layout.greeting.fallbackUserName}
                onEnabledChange={handleGreetingEnabledChange}
                onTitleChange={handleGreetingTitleChange}
                onFallbackUserNameChange={handleGreetingFallbackChange}
              />
              <AskAiSettings
                enabled={isAskAiEnabled}
                placeholder={layout.askAiInput.placeholder}
                onEnabledChange={handleAskAiEnabledChange}
                onPlaceholderChange={handleAskAiPlaceholderChange}
              />
              <SupportTicketsSettings
                enabled={isSupportTicketsEnabled}
                style={layout.supportTickets.style}
                statusesToDisplay={
                  layout.supportTickets.statusesToDisplayOnThePage
                }
                endUserTicketName={endUserTicketName}
                currentTab={currentTab}
                statusOptions={statusOptions}
                isSalesforceTicketSystemConnected={
                  isSalesforceTicketSystemConnected
                }
                allowUserReplyToTicket={
                  layout.supportTickets.allowUserReplyToTicket
                }
                onEnabledChange={handleSupportTicketsEnabledChange}
                onStyleChange={handleSupportTicketsStyleChange}
                onStatusesChange={handleSupportTicketsStatusesChange}
                onEndUserTicketNameTabChange={handleEndUserTicketNameTabChange}
                onEndUserTicketNameChange={handleEndUserTicketNameChange}
                onAllowRepliesChange={handleSupportTicketsReplyChange}
              />
            </>
          )}

          {preChatSurveyFlag && (
            <PreChatFlowSettings
              enabled={preChat.enabled}
              welcomeMessage={preChat.welcomeMessage}
              welcomeSubtitle={preChat.welcomeSubtitle}
              caseTitle={preChat.caseTitle}
              avatarSVG={preChat.avatarSVG}
              avatarRadius={preChat.avatarRadius}
              onUploadAvatar={handleUploadAvatar}
              onEnabledChange={handlePreChatEnabledChange}
              onWelcomeMessageChange={handlePreChatWelcomeMessageChange}
              onWelcomeSubtitleChange={handlePreChatWelcomeSubtitleChange}
              onCaseTitleChange={handlePreChatCaseTitleChange}
              onAvatarChange={handlePreChatAvatarChange}
              onAvatarRadiusChange={handlePreChatAvatarRadiusChange}
            />
          )}
        </Flex>
      </Flex>

      <Flex flexShrink="0">
        <QuackchatChannelLayoutPreview configuration={configuration} layout={layout} />
      </Flex>
    </Flex>
  );
}
